import { useContext, useRef, useMemo } from 'react';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { useLocation } from '@thd-olt-component-react/router';
import { parseUrl } from '../product-results-helpers';
import useAdditionalSearchParams from './useAdditionalSearchParams';

const CHECK_CAT_STYLE = /catStyle=ShowProducts/i;

const useProductResultsParamsPLP = (props) => {
  const {
    pageType,
  } = props;

  const isSearch = pageType === 'search';
  const disableSSR = typeof window !== 'undefined' && window.DISABLE_SSR;
  const varnish = !disableSSR && !isSearch;
  const store = useStore({ varnish, online: false });
  const ctx = useContext(ExperienceContext);
  const { pathname, search } = useLocation();
  const path = `${pathname}${search}`;
  const { deliveryZip: serverDeliveryZip, clientStore } = ctx;
  const { isCustomerIdentified = false } = useThdCustomer() || {};

  const SERVER_SIDE_PRODUCT_POD_COUNT = isSearch ? 24 : 12;
  const CLIENT_SIDE_PRODUCT_POD_COUNT = 24;

  const urlParams = useMemo(() => {
    const cleanPath = path.replace(/^\/render[^/]*/, '');
    return parseUrl({ path: cleanPath });
  }, [path]);

  const {
    keyword,
    lowerbound,
    Nao = 0,
    nValue,
    sortby,
    sortorder,
    upperbound,
    skipSpellCheck
  } = urlParams;

  const searchNavPropsRef = useRef({
    askForSponsored: isSearch,
    deliveryZip: serverDeliveryZip,
    pageSize: disableSSR ? CLIENT_SIDE_PRODUCT_POD_COUNT : SERVER_SIDE_PRODUCT_POD_COUNT
  });

  const totalPageSizeRef = useRef(disableSSR ? CLIENT_SIDE_PRODUCT_POD_COUNT : SERVER_SIDE_PRODUCT_POD_COUNT);
  const hasSponsoredSorts = !sortby || ['bestmatch', 'topsellers'].indexOf(sortby) > -1;
  const showProducts = CHECK_CAT_STYLE.test(path);

  let THDCustomer = {};
  if (clientStore) {
    let deliveryZipCode;
    if (
      typeof window !== 'undefined'
      && window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails
    ) {
      deliveryZipCode = window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails();
    }
    searchNavPropsRef.current = {
      pageSize: CLIENT_SIDE_PRODUCT_POD_COUNT,
      askForSponsored: true,
      deliveryZip: deliveryZipCode?.zipcode
    };
    totalPageSizeRef.current = CLIENT_SIDE_PRODUCT_POD_COUNT;

    THDCustomer = typeof window !== 'undefined' ? window.THDCustomer?.default : {};
  }

  const additionalSearchParams = useAdditionalSearchParams({
    askForSponsored: searchNavPropsRef.current?.askForSponsored,
    hasSponsoredSorts,
    deliveryZip: searchNavPropsRef.current?.deliveryZip,
    showProducts,
    skipSpellCheck
  });

  const guardedIsCustomerIdentified = store?.isLocalized ? isCustomerIdentified : false;
  // On client, skip the searchModel query until localization completes
  // This prevents duplicate calls (one before localization, one after)
  // On server, this is always false so SSR queries run normally
  const isClientSide = typeof window !== 'undefined';
  const shouldWaitForLocalization = isClientSide && !store?.isLocalized;

  // Wrap return value in useMemo to react to context/store changes
  const productResultsParams = useMemo(() => ({
    additionalSearchParams,
    keyword,
    lowerbound,
    nValue,
    start: Nao,
    storeId: store?.storeId,
    sortby,
    sortorder,
    upperbound,
    isCustomerIdentified: guardedIsCustomerIdentified,
    pageSize: searchNavPropsRef.current?.pageSize,
    path,
    isShowProducts: showProducts,
    waitForLocalization: shouldWaitForLocalization
  }), [
    additionalSearchParams,
    keyword,
    lowerbound,
    nValue,
    Nao,
    store?.storeId,
    store?.isLocalized,
    sortby,
    sortorder,
    upperbound,
    guardedIsCustomerIdentified,
    path,
    shouldWaitForLocalization
  ]);

  return productResultsParams;
};

export default useProductResultsParamsPLP;